/**
 * RevenueCat service — Car Owner "owner_premium" subscription ($3.99/mo).
 *
 * Native only (iOS = Apple IAP, Android = Google Play Billing). Every export
 * is a safe no-op / clean error on the web, so callers don't need to guard.
 *
 * This module is the SINGLE place the RevenueCat SDK gets configured.
 * `lib/iap.js` (shop Pro/Premium tiers) delegates to `ensureRevenueCatConfigured`
 * so `Purchases.configure()` is never called twice.
 *
 * SDK: @revenuecat/purchases-capacitor 11.3.2 (last release that supports
 * Capacitor 7 — v12+ requires @capacitor/core >= 8).
 */
import { Capacitor } from "@capacitor/core";

// ⚠️ JP: Replace these with your actual RevenueCat PUBLIC SDK keys from
// app.revenuecat.com → Project → Apps & providers (iOS key starts "appl_",
// Android key starts "goog_"). You can EITHER paste them here OR set
// REACT_APP_REVENUECAT_IOS_KEY / REACT_APP_REVENUECAT_ANDROID_KEY in
// frontend/.env before `npm run build` (env vars win if both are set).
// Public SDK keys are safe to ship in the app bundle (they are not secrets).
const RC_API_KEY_IOS =
  process.env.REACT_APP_REVENUECAT_IOS_KEY || "REVENUECAT_IOS_API_KEY_PLACEHOLDER";
const RC_API_KEY_ANDROID =
  process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "REVENUECAT_ANDROID_API_KEY_PLACEHOLDER";

// ⚠️ JP: This must match the entitlement identifier you set in RevenueCat dashboard
export const ENTITLEMENT_ID = "owner_premium";

// ⚠️ JP: Identifier of the RevenueCat Offering that holds the $3.99/mo owner
// package. Kept separate from the "default" offering because that one holds
// the shop Pro/Premium packages.
export const OWNER_OFFERING_ID = "owner_premium";

// ⚠️ JP: Store product ID (same string in App Store Connect + Play Console).
// Only used as a fallback when locating the package inside an offering.
export const OWNER_PRODUCT_ID = "owner_premium_monthly";

let _configured = false;
let _configuring = null;
let _loggedInAs = null;

function isPlaceholder(key) {
  return !key || key.includes("PLACEHOLDER");
}

function apiKeyForPlatform() {
  const p = Capacitor.getPlatform();
  if (p === "ios") return RC_API_KEY_IOS;
  if (p === "android") return RC_API_KEY_ANDROID;
  return "";
}

async function sdk() {
  return import("@revenuecat/purchases-capacitor");
}

/** True when running natively AND a real (non-placeholder) key is present. */
export function isRevenueCatConfigured() {
  return Capacitor.isNativePlatform() && !isPlaceholder(apiKeyForPlatform());
}

/**
 * Configure the SDK once. Safe to call repeatedly / concurrently.
 * Returns true if the SDK is ready.
 */
export async function ensureRevenueCatConfigured(appUserID) {
  if (!isRevenueCatConfigured()) return false;
  if (_configured) return true;
  if (_configuring) return _configuring;
  _configuring = (async () => {
    try {
      const { Purchases, LOG_LEVEL } = await sdk();
      await Purchases.setLogLevel({
        level: process.env.NODE_ENV !== "production" ? LOG_LEVEL.DEBUG : LOG_LEVEL.WARN,
      });
      const opts = { apiKey: apiKeyForPlatform() };
      if (appUserID) opts.appUserID = String(appUserID);
      await Purchases.configure(opts);
      _configured = true;
      if (appUserID) _loggedInAs = String(appUserID);
      return true;
    } catch (e) {
      console.error("RevenueCat configure failed:", e);
      return false;
    } finally {
      _configuring = null;
    }
  })();
  return _configuring;
}

/**
 * Configure + identify the signed-in user. Call after login.
 * `userId` MUST be our backend `user.user_id` — the webhook maps
 * RevenueCat's `app_user_id` straight to `users.user_id`.
 */
export async function initRevenueCat(userId) {
  if (!Capacitor.isNativePlatform()) return;
  if (!isRevenueCatConfigured()) {
    console.info("RevenueCat: API key placeholder not replaced yet — IAP disabled.");
    return;
  }
  const ok = await ensureRevenueCatConfigured(userId);
  if (!ok || !userId) return;
  const id = String(userId);
  if (_loggedInAs === id) return;
  try {
    const { Purchases } = await sdk();
    await Purchases.logIn({ appUserID: id });
    _loggedInAs = id;
  } catch (e) {
    console.warn("RevenueCat logIn failed:", e);
  }
}

/** Call on logout so the next user doesn't inherit the previous entitlements. */
export async function logOutRevenueCat() {
  if (!_configured || !_loggedInAs) return;
  try {
    const { Purchases } = await sdk();
    const { isAnonymous } = await Purchases.isAnonymous();
    if (!isAnonymous) await Purchases.logOut();
  } catch (e) {
    console.debug("RevenueCat logOut:", e?.message || e);
  } finally {
    _loggedInAs = null;
  }
}

/** Returns the owner offering (dedicated offering first, then `current`). */
export async function getOwnerPremiumOffering() {
  if (!(await ensureRevenueCatConfigured())) return null;
  const { Purchases } = await sdk();
  const offerings = await Purchases.getOfferings();
  return offerings?.all?.[OWNER_OFFERING_ID] || offerings?.current || null;
}

/** Picks the $3.99 monthly owner package out of an offering. */
export function pickOwnerPackage(offering) {
  if (!offering) return null;
  const pkgs = offering.availablePackages || [];
  const byProduct = (p) => {
    const id = (p?.product?.identifier || "").toLowerCase();
    return id === OWNER_PRODUCT_ID || id.startsWith(`${OWNER_PRODUCT_ID}:`) || id.includes("owner");
  };
  return (
    pkgs.find(byProduct)
    || (offering.identifier === OWNER_OFFERING_ID ? (offering.monthly || pkgs[0]) : null)
    || null
  );
}

/** Best-effort human-readable trial text from the store product, e.g. "7-day free trial". */
export function describeTrial(pkg) {
  const product = pkg?.product;
  if (!product) return null;
  const unitWord = { DAY: "day", WEEK: "week", MONTH: "month", YEAR: "year" };
  // iOS: introductory offer
  const intro = product.introPrice;
  if (intro && Number(intro.price) === 0 && intro.periodNumberOfUnits) {
    const n = intro.periodNumberOfUnits;
    const u = unitWord[intro.periodUnit] || "day";
    return `${n}-${u} free trial`;
  }
  // Android: free phase of the default subscription option (ISO-8601 period, e.g. P7D / P1W)
  const free = product.defaultOption?.freePhase;
  const iso = free?.billingPeriod?.iso8601;
  const m = typeof iso === "string" && iso.match(/^P(\d+)([DWMY])$/);
  if (m) {
    const u = { D: "day", W: "week", M: "month", Y: "year" }[m[2]];
    return `${m[1]}-${u} free trial`;
  }
  return null;
}

export function isOwnerPremiumActive(customerInfo) {
  return customerInfo?.entitlements?.active?.[ENTITLEMENT_ID] !== undefined;
}

/**
 * Purchase the owner package.
 * Resolves { active, customerInfo } or { userCancelled: true }. Throws on real errors.
 */
export async function purchaseOwnerPremium(packageToPurchase) {
  if (!(await ensureRevenueCatConfigured())) {
    throw new Error("In-app purchases aren't available yet. Please try again later.");
  }
  if (!packageToPurchase) throw new Error("Subscription is not available right now.");
  const { Purchases } = await sdk();
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: packageToPurchase });
    return { active: isOwnerPremiumActive(customerInfo), customerInfo };
  } catch (e) {
    if (e?.userCancelled || e?.code === "1" || e?.code === 1) return { userCancelled: true };
    throw e;
  }
}

/** Restore purchases (Apple requires a visible Restore button). */
export async function restorePurchases() {
  if (!(await ensureRevenueCatConfigured())) {
    throw new Error("In-app purchases aren't available yet. Please try again later.");
  }
  const { Purchases } = await sdk();
  const { customerInfo } = await Purchases.restorePurchases();
  return { active: isOwnerPremiumActive(customerInfo), customerInfo };
}

export async function getCustomerInfo() {
  if (!(await ensureRevenueCatConfigured())) return null;
  const { Purchases } = await sdk();
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}
